// Grids & Gantts — the Rescheduler's marks on the page (the rules are
// ./reschedule.js; the wiring is Structure.jsx).
//
//   RescheduleIcon    the drawing: a bar with the bars under it, and the
//                     arrow they all move along together
//   RescheduleBadge   the icon BESIDE a bar whose work type carries the work
//                     under it, with a hover card that says what a drag will
//                     do — how much hangs under it, on how many levels, and
//                     what this work type's setting leaves where it is
//   RescheduleChip    the running count in the toolbar while a move saves,
//                     and its summary once it has
//   RescheduleReport  what moved, what stayed and why, what was refused

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FONT } from './theme';
import { CATEGORY_LABEL, scopeSentence, signedDays } from './reschedule';

export function RescheduleIcon({ size = 14, color = 'currentColor', style }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true" fill="none"
      style={{ display: 'inline-block', verticalAlign: 'middle', flex: '0 0 auto', ...style }}>
      {/* the bar, and the two bars that hang under it */}
      <rect x="1" y="1.6" width="8.4" height="3.2" rx="1.1" fill={color} />
      <path d="M2.6 4.8v7.4M2.6 8.4h2" stroke={color} strokeWidth="1.1" strokeLinecap="round" />
      <rect x="4.6" y="7" width="5.2" height="2.8" rx="1" fill={color} opacity="0.72" />
      <path d="M2.6 12.2h2" stroke={color} strokeWidth="1.1" strokeLinecap="round" />
      <rect x="4.6" y="10.8" width="6.2" height="2.8" rx="1" fill={color} opacity="0.72" />
      {/* the arrow they move along */}
      <path d="M11.2 3.2h3.6M13.3 1.6l1.6 1.6-1.6 1.6" stroke={color} strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const levelWords = (n) => `${n} level${n === 1 ? '' : 's'}`;
const items = (n) => `${Number(n || 0).toLocaleString()} work item${n === 1 ? '' : 's'}`;

// What the card says, from the rule and the plan (or the plan still coming).
function CardBody({ ui, itemKey, typeName, rule, info }) {
  const plan = info && info.plan;
  const err = info && info.error;
  const c = plan && plan.counts;
  const line = { margin: '0 0 7px', lineHeight: 1.45 };
  const label = { fontSize: 10.5, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: ui.muted, margin: '9px 0 4px' };
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 7 }}>
        <span style={{ display: 'inline-flex', width: 22, height: 22, borderRadius: 6, alignItems: 'center', justifyContent: 'center', background: `${ui.blue}1a`, color: ui.blue }}>
          <RescheduleIcon size={14} />
        </span>
        <span style={{ fontWeight: 800, fontSize: 13 }}>Rescheduler</span>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: ui.muted, fontWeight: 600 }}>{typeName}</span>
      </div>
      <p style={line}>
        Drag this bar <strong>whole</strong> and every work item under <strong>{itemKey}</strong> moves
        with it, by the same number of days.
      </p>
      {err ? <p style={{ ...line, color: ui.red }}>{err}</p> : null}
      {!err && !plan ? <p style={{ ...line, color: ui.muted }}>Reading the work under it…</p> : null}
      {plan ? (
        <p style={line}>
          {c.under ? (
            <>
              <span style={{ color: ui.green, fontWeight: 700 }}>{items(c.moving)} move</span>
              {c.done ? <> · <span style={{ fontWeight: 700 }}>{c.done} Done</span> stay</> : null}
              {c.inProgress ? <> · <span style={{ fontWeight: 700 }}>{c.inProgress} In Progress</span> stay</> : null}
              {c.noDates ? <> · {c.noDates} without dates</> : null}
            </>
          ) : <>Nothing hangs under it — only the bar itself moves.</>}
        </p>
      ) : null}
      <div style={label}>Setting for {typeName}</div>
      <p style={line}>{scopeSentence(rule)}</p>
      <p style={{ ...line, margin: '8px 0 0', paddingTop: 8, borderTop: `1px solid ${ui.border}`, color: ui.muted, fontSize: 11.5 }}>
        Dragging only one end of the bar — only the Start date or only the Due date — changes this work item alone.
      </p>
    </>
  );
}

// The card, floated over everything beside the icon and kept on screen.
function FloatingCard({ anchor, ui, children, onEnter, onLeave, width = 300 }) {
  const ref = useRef(null);
  const [pos, setPos] = useState(null);
  useLayoutEffect(() => {
    if (!anchor) return;
    const a = anchor.getBoundingClientRect();
    const h = ref.current ? ref.current.offsetHeight : 220;
    const vw = window.innerWidth; const vh = window.innerHeight;
    let left = a.right + 8;
    if (left + width > vw - 8) left = Math.max(8, a.left - width - 8);
    let top = a.top - 10;
    if (top + h > vh - 8) top = Math.max(8, vh - h - 8);
    setPos({ left, top });
  }, [anchor, width, children]);
  if (!anchor || typeof document === 'undefined') return null;
  return createPortal(
    <div ref={ref} role="tooltip" onMouseEnter={onEnter} onMouseLeave={onLeave}
      style={{
        position: 'fixed', zIndex: 10050, left: pos ? pos.left : -9999, top: pos ? pos.top : -9999, width,
        background: ui.card, color: ui.fg, border: `1px solid ${ui.border}`, borderRadius: 10,
        boxShadow: ui.shadowMd, padding: '11px 13px', fontFamily: FONT, fontSize: 12, textAlign: 'left',
      }}>
      {children}
    </div>,
    document.body,
  );
}

// The icon beside a bar. Hovering it reads the plan (once, then cached by
// the page) and opens the card; leaving it lets the card go a moment later,
// so the mouse can travel onto the card.
export function RescheduleBadge({ ui, row, rule, info, onIntent, style, active = false }) {
  const [anchor, setAnchor] = useState(null);
  const shut = useRef(0);
  const open = (el) => { if (shut.current) { clearTimeout(shut.current); shut.current = 0; } setAnchor(el); if (onIntent) onIntent(row); };
  const close = () => { if (shut.current) clearTimeout(shut.current); shut.current = setTimeout(() => setAnchor(null), 160); };
  useEffect(() => () => { if (shut.current) clearTimeout(shut.current); }, []);
  const typeName = (row && row.it && row.it.fields && row.it.fields.issuetype && row.it.fields.issuetype.name) || '';
  return (
    <>
      <span role="img" aria-label={`Rescheduler — dragging ${row.key} whole moves the work under it`}
        onMouseEnter={(e) => open(e.currentTarget)} onMouseLeave={close}
        onMouseDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}
        style={{
          position: 'absolute', top: 'calc(50% - 9px)', width: 18, height: 18, borderRadius: 5,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'help',
          color: active ? '#fff' : ui.blue, background: active ? ui.blue : ui.card,
          border: `1px solid ${active ? ui.blue : `${ui.blue}66`}`, boxShadow: ui.shadowXs, zIndex: 2,
          ...style,
        }}>
        <RescheduleIcon size={12} />
      </span>
      {anchor ? (
        <FloatingCard anchor={anchor} ui={ui} onEnter={() => open(anchor)} onLeave={close}>
          <CardBody ui={ui} itemKey={row.key} typeName={typeName} rule={rule} info={info} />
        </FloatingCard>
      ) : null}
    </>
  );
}

// ---- the toolbar chip and the report ----------------------------------------------
export function RescheduleChip({ ui, S, job, onOpen, onDismiss }) {
  if (!job) return null;
  const running = job.status === 'running';
  const failedRun = job.status === 'failed';
  const refused = (job.failed || []).length;
  const text = running
    ? `Rescheduling ${job.key} ${signedDays(job.days)} · ${job.done || 0}/${job.total == null ? '…' : job.total}`
    : (failedRun ? `${job.key} was not rescheduled`
      : `Rescheduled ${job.key} ${signedDays(job.days)} · ${items((job.moved || []).length + 1)}${refused ? ` · ${refused} refused` : ''}`);
  const color = running ? ui.blue : (failedRun || refused ? ui.red : ui.green);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 0 }}>
      <button onClick={onOpen} aria-label="Open the reschedule report"
        style={{ ...S.exportBtn, display: 'inline-flex', alignItems: 'center', gap: 6, color, borderColor: `${color}66`, borderTopRightRadius: running ? 999 : 0, borderBottomRightRadius: running ? 999 : 0, fontVariantNumeric: 'tabular-nums' }}>
        <RescheduleIcon size={13} />
        {text}
      </button>
      {!running ? (
        <button onClick={onDismiss} aria-label="Dismiss"
          style={{ ...S.exportBtn, borderLeft: 'none', borderTopLeftRadius: 0, borderBottomLeftRadius: 0, padding: '5px 8px' }}>×</button>
      ) : null}
    </span>
  );
}

const WHY = { done: 'Done — this work type leaves Done work where it is', inProgress: 'In Progress — this work type leaves In Progress work where it is', noDates: 'no Start date and no Due date' };

export function RescheduleReport({ ui, S, job, onClose, onOpenKey }) {
  if (!job) return null;
  const staying = job.staying || [];
  const failed = job.failed || [];
  const keyBtn = (k) => (
    <button key={k} onClick={() => onOpenKey && onOpenKey(k)}
      style={{ border: 'none', background: 'none', padding: 0, color: ui.blue, fontWeight: 700, cursor: 'pointer', fontFamily: FONT, fontSize: 12 }}>{k}</button>
  );
  const group = (list, why) => list.filter((x) => x.why === why);
  return (
    <div role="dialog" aria-label="Reschedule report"
      style={{ position: 'fixed', right: 24, top: 120, zIndex: 10040, width: 420, maxHeight: '70vh', overflowY: 'auto', background: ui.card, color: ui.fg, border: `1px solid ${ui.border}`, borderRadius: 12, boxShadow: ui.shadowMd, padding: '14px 16px', fontFamily: FONT, fontSize: 12.5 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <RescheduleIcon size={16} color={ui.blue} />
        <strong style={{ fontSize: 14 }}>Rescheduled {job.key} {signedDays(job.days)}</strong>
        <button onClick={onClose} aria-label="Close" style={{ marginLeft: 'auto', ...S.exportBtn, padding: '2px 9px' }}>×</button>
      </div>
      {job.status === 'running' ? <p style={{ margin: '0 0 8px', color: ui.blue }}>Saving {job.done || 0} of {job.total == null ? '…' : job.total}…</p> : null}
      {job.error ? <p style={{ margin: '0 0 8px', color: ui.red }}>{job.error}</p> : null}
      {job.levels != null ? (
        <p style={{ margin: '0 0 8px', lineHeight: 1.5 }}>
          The bar and {items((job.moved || []).length)} under it moved
          {job.levels ? <> — {levelWords(job.levels)} deep</> : null}
          {job.seconds != null ? <span style={{ color: ui.muted }}> · {job.seconds.toFixed(1)} s</span> : null}.
        </p>
      ) : null}
      {failed.length ? (
        <>
          <div style={{ ...S.label, marginTop: 10, color: ui.red }}>Refused — still on their old dates ({failed.length})</div>
          {failed.map((f) => <div key={f.key} style={{ margin: '0 0 4px', lineHeight: 1.4 }}>{keyBtn(f.key)} <span style={{ color: ui.muted }}>{f.reason}</span></div>)}
        </>
      ) : null}
      {['done', 'inProgress', 'noDates'].map((why) => {
        const list = group(staying, why);
        if (!list.length) return null;
        return (
          <div key={why}>
            <div style={{ ...S.label, marginTop: 10 }}>Left where they were — {why === 'noDates' ? 'no dates' : CATEGORY_LABEL[why]} ({list.length})</div>
            <div style={{ color: ui.muted, margin: '0 0 4px' }}>{WHY[why]}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{list.slice(0, 60).map((x) => keyBtn(x.key))}{list.length > 60 ? <span style={{ color: ui.muted }}>and {list.length - 60} more</span> : null}</div>
          </div>
        );
      })}
    </div>
  );
}
